import * as React from "react";
import ReactTable from "react-table";
import {formatDate} from "../../../../../../format";
import {ConfigContext} from "../../../../context/ConfigContext";
import {CompanyConfigHistory, CompanyHistory, Contract} from "../../../../../../generated";
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";
import {ContractHistoryModal} from "../edit/modals/ContractHistoryModal";
import {Button, Icon, Input} from "semantic-ui-react";
import {displayTicketNrs} from "../../../configChange/configEdit/rightSide/HistoryTable";

export interface ContractsHistoryListProps {
}

export function ContractsHistoryList(props: ContractsHistoryListProps) {
    const context = React.useContext(ConfigContext);

    const [isLoading, setIsLoading] = React.useState(true);
    const [history, setHistory] = React.useState<CompanyHistory[] | null>(null);
    const [selectedHistory, setSelectedHistory] = React.useState<CompanyHistory | null>(null);
    const [followingContract, setFollowingContract] = React.useState<Contract | null>(null);
    const [showChanges, setShowChanges] = React.useState(false);
    const [showRollBackView, setShowRollBackView] = React.useState(false);
    const [commentQuery, setCommentQuery] = React.useState<string | null>(null);

    // Table
    const [take, setTake] = React.useState(10);
    const [page, setPage] = React.useState(1);
    const [sortBy, setSortBy] = React.useState<"TIMESTAMP" | "CHANGED_BY" | "COMMENT">("TIMESTAMP");
    const [sortDirection, setSortDirection] = React.useState<"DESC" | "ASC">("DESC");
    const [count, setCount] = React.useState(-1);

    React.useEffect(() => {
        fetchHistory().then();
    }, []);

    async function fetchHistory(takeParam = take, pageParam = page, cQuery = commentQuery) {
        setIsLoading(true);
        const auth = backend.withTokenAuthHeader(authentication.token);
        const historyData: CompanyConfigHistory = await backend.internalApi.fetchCompanyConfigurationHistory(
            context.companyId,
            cQuery,
            takeParam,
            (pageParam - 1) * takeParam,
            sortBy,
            sortDirection,
            null,
            'CONTRACT_ONLY',
            auth
        );
        setHistory(historyData.results);
        setCount(historyData.count);
        setIsLoading(false);
        setTake(takeParam);
        setPage(pageParam);
        context.setHistory(historyData);
    }

    async function changeSort(sortByParam: 'TIMESTAMP' | 'CHANGED_BY' | 'COMMENT' | 'NONE') {
        setIsLoading(true);
        if (sortByParam === 'NONE') return;
        if (sortBy === sortByParam) {
            setSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setSortBy(sortByParam as any);
            setSortDirection("ASC");
        }
        await fetchHistory();
        setIsLoading(false);
    }

    function changePage(pageIndex: number) {
        setIsLoading(true);
        fetchHistory(take, pageIndex + 1);
    }

    function changePageSize(newSize: number) {
        setIsLoading(true);
        fetchHistory(newSize, page);
    }

    function fillDummyEntries(historyArr: CompanyHistory[]) {
        const totalAmount = historyArr.length;
        const dummy = {} as CompanyHistory;
        const currentEntries: CompanyHistory[] = [];
        const indexOffset = (page - 1) * take;
        const firstActualFileIndex = indexOffset;
        const lastActualFileIndex = indexOffset + take - 1;

        for (let i = 0; i < totalAmount; i++) {
            if (i < firstActualFileIndex) {
                currentEntries.push(dummy);
            } else if (i >= firstActualFileIndex) {
                if (i <= lastActualFileIndex) {
                    currentEntries.push(historyArr[i - indexOffset]);
                } else {
                    currentEntries.push(dummy);
                }
            }
        }
        while (currentEntries.length < totalAmount) {
            currentEntries.push(dummy);
        }
        return currentEntries;
    }

    async function openChanges(row: CompanyHistory) {
        const auth = backend.withTokenAuthHeader(authentication.token);
        const response = await backend.internalApi.fetchFollowingCompanyContractHistory(context.companyId, row.contract._id, row._id, auth);
        setShowChanges(true);
        setSelectedHistory(row);
        setFollowingContract(response.followingContract);
    }

    if (history == null) {
        return <p>No histories found</p>;
    } else {
        const filledHistory: CompanyHistory[] | null = fillDummyEntries(history);
        const columns = [
            {
                id: 'ID',
                Header: 'id',
                width: 270,
                accessor: (d: any) => d.contract._id,
                sortField: 'ID'
            },
            {
                id: 'VALID_FROM',
                Header: 'valid from',
                width: 180,
                accessor: (d: any) => d.contract.validFrom,
                sortField: 'VALID_FROM'
            },
            {
                id: 'TIMESTAMP',
                Header: 'timestamp',
                width: 150,
                accessor: (d: any) => d.timestamp,
                Cell: (d: any) => <span>{formatDate(d.value)}</span>,
                sortMethod: (a: any, b: any) => {
                    let a1 = new Date(a).getTime();
                    let b1 = new Date(b).getTime();
                    if (a1 < b1) return 1;
                    else if (a1 > b1) return -1;
                    else return 0;
                },
                sortField: 'TIMESTAMP'
            },
            {
                id: 'CHANGED_BY',
                Header: 'changedBy',
                width: 200,
                accessor: (d: any) => d.changedBy,
                sortField: 'CHANGED_BY'
            },
            {
                id: 'TICKET_NRS',
                Header: 'TicketNrs',
                width: 200,
                accessor: (d: any) => displayTicketNrs(d.ticketNrs),
                sortField: 'TICKET_NRS'
            },
            {
                id: 'COMMENT',
                Header: 'comment',
                accessor: (d: any) => d.changeComment,
                sortField: 'COMMENT'
            }
        ];
        function drawSearchBar() {
            return (
                <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center" }}>
                    <Input
                        icon="search"
                        placeholder="Search..."
                        onChange={(e: any) => {
                            setCommentQuery(e.target.value)
                        }}
                        value={commentQuery}
                        style={{ flex: 1 }}
                        action={
                            <Button
                                icon
                                onClick={() => {
                                    fetchHistory().then();
                                }}
                            >
                                <Icon name="search" />
                            </Button>
                        }
                    />
                    <Button
                        icon
                        disabled={commentQuery === ""}
                        onClick={() => {
                            setCommentQuery("");
                            fetchHistory(10, 1, "").then();
                        }}
                        style={{ marginLeft: 8 }}
                    >
                        <Icon name="trash" />
                    </Button>
                </div>
            );
        }
        return (
            <>
                {drawSearchBar()}
                <ReactTable
                    data={filledHistory}
                    pages={Math.ceil(count / take)}
                    columns={columns}
                    sorted={[
                        {
                            id: 'TIMESTAMP',
                            desc: false
                        }
                    ]}
                    onSortedChange={(newSorted, column, shiftKey) => {
                        changeSort(column.sortField).then();
                    }}
                    defaultPageSize={take}
                    className="-striped -highlight"
                    loading={isLoading}
                    getTdProps={(state, rowInfo, column, instance) => ({
                        onClick: () => {
                            if (rowInfo) {
                                if(column.id != "TICKET_NRS") {
                                    openChanges(rowInfo.row._original).then();
                                }
                            }
                        }
                    })}
                    style={{cursor: "pointer"}}
                    onPageChange={changePage}
                    onPageSizeChange={changePageSize}
                />

                <ContractHistoryModal
                    isOpen={showChanges}
                    onClose={async (openRollback: boolean, commentDidChange: boolean) => {
                        if (openRollback) {
                            setShowChanges(false);
                            setShowRollBackView(true);
                        } else {
                            setShowChanges(false);
                        }
                        if (commentDidChange) {
                            await fetchHistory();
                        }
                    }}
                    history={selectedHistory}
                    followingContractVersion={followingContract}
                />
            </>
        );
    }
}